import React, { useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw, Users, Phone, Mail, Building2, MessageCircle, Calendar, Search, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passError, setPassError] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setPassError(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        setAuthenticated(true);
        sessionStorage.setItem('milyan_admin', 'true');
      } else {
        setPassError(true);
      }
    } catch {
      setPassError(true);
    }
    setLoginLoading(false);
  };

  useEffect(() => {
    if (sessionStorage.getItem('milyan_admin') === 'true') {
      setAuthenticated(true);
    }
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`);
      if (res.ok) {
        const data = await res.json();
        setLeads(data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (err) {
      console.error('Erro ao buscar leads:', err);
    }
    setLoading(false);
  };

  useEffect(() => { if (authenticated) fetchLeads(); }, [authenticated]);

  const filtered = leads.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.phone.includes(search) ||
    (l.email && l.email.toLowerCase().includes(search.toLowerCase())) ||
    (l.company && l.company.toLowerCase().includes(search.toLowerCase()))
  );

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-raisin-black text-white flex items-center justify-center px-6">
        <Card className="w-full max-w-sm p-8 bg-white/5 border-slate-blue/30" data-testid="admin-login-card">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-slate-blue/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-slate-blue" />
            </div>
            <h1 className="text-2xl font-bold">Painel Admin</h1>
            <p className="text-sm text-gray-400 mt-1">Digite a senha para acessar</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPassError(false); }}
              placeholder="Senha"
              data-testid="admin-password-input"
              className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors ${passError ? 'border-red-500 focus:border-red-500' : 'border-slate-blue/30 focus:border-chartreuse/50'}`}
              autoFocus
            />
            {passError && <p className="text-red-400 text-sm">Senha incorreta</p>}
            <Button type="submit" data-testid="admin-login-button" className="w-full bg-slate-blue hover:bg-slate-blue/90 font-bold">
              Entrar
            </Button>
          </form>
          <button onClick={() => navigate('/')} className="w-full text-center text-sm text-gray-500 hover:text-gray-300 mt-4 transition-colors">
            Voltar ao site
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-raisin-black text-white">
      <header className="bg-raisin-black/95 backdrop-blur-md border-b border-slate-blue/20 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Voltar ao site
            </Button>
            <h1 className="text-xl font-bold">Painel de Leads</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchLeads}
              variant="outline"
              className="border-slate-blue/40 text-gray-300 hover:text-white"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              onClick={() => { sessionStorage.removeItem('milyan_admin'); setAuthenticated(false); }}
              variant="ghost"
              className="text-gray-500 hover:text-red-400"
              data-testid="admin-logout-button"
            >
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-white/5 border-slate-blue/20" data-testid="stat-total">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-slate-blue" />
              <div>
                <div className="text-2xl font-bold">{leads.length}</div>
                <div className="text-xs text-gray-400">Total de Leads</div>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white/5 border-slate-blue/20" data-testid="stat-orion">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-blue/20 flex items-center justify-center text-sm font-bold text-slate-blue">O</div>
              <div>
                <div className="text-2xl font-bold">{leads.filter(l => l.plan && l.plan.includes('Orion')).length}</div>
                <div className="text-xs text-gray-400">Orion</div>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white/5 border-slate-blue/20" data-testid="stat-sirius">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-chartreuse/20 flex items-center justify-center text-sm font-bold text-chartreuse">S</div>
              <div>
                <div className="text-2xl font-bold">{leads.filter(l => l.plan && l.plan.includes('Sirius')).length}</div>
                <div className="text-xs text-gray-400">Sirius</div>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white/5 border-slate-blue/20" data-testid="stat-milyan">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-giants-orange/20 flex items-center justify-center text-sm font-bold text-giants-orange">M</div>
              <div>
                <div className="text-2xl font-bold">{leads.filter(l => l.plan && l.plan.includes('Milyan')).length}</div>
                <div className="text-xs text-gray-400">Milyan</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por nome, telefone, email ou empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search-input"
            className="w-full pl-12 pr-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors"
          />
        </div>

        {/* Leads Table */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Carregando leads...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400" data-testid="admin-empty-state">
            {search ? 'Nenhum lead encontrado para essa busca.' : 'Nenhum lead ainda. Os leads aparecerão aqui quando alguém preencher o formulário.'}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((lead) => (
              <Card
                key={lead.id}
                data-testid={`lead-card-${lead.id}`}
                className="p-5 bg-white/5 border-slate-blue/20 hover:border-slate-blue/40 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-lg font-bold">{lead.name}</span>
                      {lead.plan && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          lead.plan.includes('Orion') ? 'bg-slate-blue/20 text-slate-blue' :
                          lead.plan.includes('Sirius') ? 'bg-chartreuse/20 text-chartreuse' :
                          lead.plan.includes('Milyan') ? 'bg-giants-orange/20 text-giants-orange' :
                          'bg-white/10 text-gray-400'
                        }`}>
                          {lead.plan}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{lead.phone}</span>
                      {lead.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{lead.email}</span>}
                      {lead.company && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{lead.company}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(lead.created_at)}</span>
                    </div>
                    {lead.message && (
                      <p className="text-sm text-gray-300 mt-1 bg-white/5 rounded-lg p-3">
                        <MessageCircle className="w-3.5 h-3.5 inline mr-1 text-gray-500" />
                        {lead.message}
                      </p>
                    )}
                  </div>
                  <a
                    href={`https://wa.me/55${lead.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`whatsapp-link-${lead.id}`}
                  >
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Button>
                  </a>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
